export type LatexSegment =
  | { type: "text"; value: string }
  | { type: "math"; value: string; display: boolean };

const greek: Record<string, string> = {
  alpha: "α",
  beta: "β",
  gamma: "γ",
  delta: "δ",
  epsilon: "ε",
  theta: "θ",
  lambda: "λ",
  mu: "μ",
  nu: "ν",
  pi: "π",
  rho: "ρ",
  sigma: "σ",
  tau: "τ",
  phi: "φ",
  chi: "χ",
  psi: "ψ",
  omega: "ω",
  Delta: "Δ",
  Gamma: "Γ",
  Theta: "Θ",
  Lambda: "Λ",
  Pi: "Π",
  Sigma: "Σ",
  Phi: "Φ",
  Psi: "Ψ",
  Omega: "Ω",
};

const operators: Record<string, string> = {
  times: "×",
  cdot: "·",
  pm: "±",
  mp: "∓",
  approx: "≈",
  sim: "∼",
  neq: "≠",
  le: "≤",
  leq: "≤",
  ge: "≥",
  geq: "≥",
  infty: "∞",
  to: "→",
  rightarrow: "→",
  leftarrow: "←",
  ldots: "…",
  degree: "°",
};

const escapeXml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

export function splitLatexText(text: string): LatexSegment[] {
  const segments: LatexSegment[] = [];
  // Legacy/imported activities also use dollar delimiters. Inline dollars
  // cannot span lines or close before a digit (ordinary currency).
  const pattern = /\\\[([\s\S]*?)\\\]|\\\(([\s\S]*?)\\\)|(?<![\\$])\$\$([\s\S]*?)(?<!\\)\$\$(?!\$)|(?<![\\$])\$(?![\s$])((?:\\.|[^\\$\r\n])+?)(?<!\s)\$(?![\d$])/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text))) {
    if (match.index > last)
      segments.push({ type: "text", value: text.slice(last, match.index) });
    segments.push({
      type: "math",
      value: (match[1] ?? match[2] ?? match[3] ?? match[4] ?? "").trim(),
      display: match[1] !== undefined || match[3] !== undefined,
    });
    last = pattern.lastIndex;
  }
  if (last < text.length) segments.push({ type: "text", value: text.slice(last) });
  return segments.length ? segments : [{ type: "text", value: text }];
}

class Parser {
  private i = 0;
  constructor(private readonly source: string,private readonly upright=false) {}

  parse() {
    return this.sequence();
  }

  private sequence(stop?: string): string {
    const nodes: string[] = [];
    while (this.i < this.source.length) {
      if (stop && this.source[this.i] === stop) {
        this.i++;
        break;
      }
      if (/\s/.test(this.source[this.i])) {
        this.i++;
        continue;
      }
      let atom = this.atom();
      if (!atom) continue;
      let sub: string | undefined;
      let sup: string | undefined;
      while (this.i < this.source.length && (this.source[this.i] === "_" || this.source[this.i] === "^")) {
        const type = this.source[this.i++];
        const script = this.groupOrAtom();
        if (type === "_") sub = script;
        else sup = script;
      }
      if (sub && sup) atom = `<msubsup>${atom}${sub}${sup}</msubsup>`;
      else if (sub) atom = `<msub>${atom}${sub}</msub>`;
      else if (sup) atom = `<msup>${atom}${sup}</msup>`;
      nodes.push(atom);
    }
    return nodes.join("");
  }

  private groupOrAtom(): string {
    while (this.i < this.source.length && /\s/.test(this.source[this.i])) this.i++;
    if (this.source[this.i] === "{") {
      this.i++;
      return `<mrow>${this.sequence("}")}</mrow>`;
    }
    return this.atom();
  }

  private rawGroup(): string {
    while (this.i < this.source.length && /\s/.test(this.source[this.i])) this.i++;
    if (this.source[this.i] !== "{") return "";
    this.i++;
    const start = this.i;
    let depth = 1;
    while (this.i < this.source.length && depth > 0) {
      if (this.source[this.i] === "{") depth++;
      else if (this.source[this.i] === "}") depth--;
      this.i++;
    }
    return this.source.slice(start, Math.max(start, this.i - 1));
  }

  private atom(): string {
    if (this.i >= this.source.length) return "";
    const ch = this.source[this.i];
    if (ch === "{") {
      this.i++;
      return `<mrow>${this.sequence("}")}</mrow>`;
    }
    if (ch === "\\") return this.command();
    if (/\d/.test(ch)) {
      const start = this.i++;
      while (this.i < this.source.length && /[\d.,]/.test(this.source[this.i])) this.i++;
      return `<mn>${escapeXml(this.source.slice(start, this.i))}</mn>`;
    }
    if (/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(ch)) {
      this.i++;
      return `<mi${this.upright?' mathvariant="normal"':''}>${escapeXml(ch)}</mi>`;
    }
    this.i++;
    if ("=+-−*/()[]|,:;<>".includes(ch)) return `<mo>${escapeXml(ch)}</mo>`;
    return `<mtext>${escapeXml(ch)}</mtext>`;
  }

  private command(): string {
    this.i++;
    const start = this.i;
    if (/[A-Za-z]/.test(this.source[this.i] ?? "")) {
      while (this.i < this.source.length && /[A-Za-z]/.test(this.source[this.i])) this.i++;
    } else {
      this.i++;
    }
    const name = this.source.slice(start, this.i);
    if (name === "frac" || name === "dfrac" || name === "tfrac") {
      const numerator = this.groupOrAtom();
      const denominator = this.groupOrAtom();
      return `<mfrac>${numerator}${denominator}</mfrac>`;
    }
    if (name === "sqrt") return `<msqrt>${this.groupOrAtom()}</msqrt>`;
    if (name === "boxed") return `<menclose notation="box">${this.groupOrAtom()}</menclose>`;
    if (name === "mathrm") {
      const value = this.rawGroup();
      return /[\\_^{}]/.test(value)
        ? `<mstyle mathvariant="normal"><mrow>${new Parser(value,true).parse()}</mrow></mstyle>`
        : `<mtext>${escapeXml(value)}</mtext>`;
    }
    if (name === "text" || name === "operatorname")
      return `<mtext>${escapeXml(this.rawGroup())}</mtext>`;
    if (name === "left" || name === "right") {
      while (this.i < this.source.length && /\s/.test(this.source[this.i])) this.i++;
      if (this.source[this.i] === "\\") return this.command();
      if (this.i < this.source.length) return `<mo fence="true">${escapeXml(this.source[this.i++])}</mo>`;
      return "";
    }
    if (name === "," || name === ";" || name === ":" || name === "quad" || name === "qquad")
      return `<mspace width="${name === "qquad" ? "2em" : name === "quad" ? "1em" : "0.25em"}"/>`;
    if (name === " " || name === "!") return "";
    if (greek[name]) return `<mi>${greek[name]}</mi>`;
    if (operators[name]) return `<mo>${operators[name]}</mo>`;
    if (["{", "}", "_", "%", "#", "&", "$"].includes(name)) return `<mo>${escapeXml(name)}</mo>`;
    return `<mtext>${escapeXml(`\\${name}`)}</mtext>`;
  }
}

export function latexToMathMl(latex: string, display = false): string {
  const body = new Parser(latex).parse();
  return `<math xmlns="http://www.w3.org/1998/Math/MathML"${display ? ' display="block"' : ""}><mrow>${body}</mrow></math>`;
}
